import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SideNavItem from './item/item';
import GmModal from 'app/shared/modal/modal';
import ListCreationForm from 'app/member/modules/mailing-lists/create/create';
import TopupForm from 'app/member/modules/wallet';
import { Icon, SiteIcon } from 'assets/icons';

import styles from './side-nav.module.css';
import useScreenSize from 'hooks/size';
import { PermissionsContext } from 'contexts/permissions';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { SubscriptionPurchaseContext } from 'contexts/checkout/subscription';
import { nav } from './config.js';
import { SwitchOrganizationModal } from '../organization/switch';
import { logout } from 'store/actions/user-data';
import { useGuestService } from 'hooks/guest';
import { useDispatch, useSelector } from 'react-redux';
import { TenantContext } from 'contexts/tenant';
import profile_icon from 'assets/images/profile_icon.png';
import { truncateText } from 'app/shared/utils/general';

const SideNav = (props) => {
  const { open_tray, onTrayClose } = props;
  const { permissions } = useContext(PermissionsContext);
  const { setModuleCode, openModal } = useContext(SubscriptionPurchaseContext);
  const dispatch = useDispatch();
  const history = useNavigate();
  const veil = useRef();
  const { profile_image } = useSelector((state) => state.user_data);
  const { isDesktop, isMiniDesktop } = useScreenSize();

  const { logOut } = useGuestService();
  const { fetchSubscription } = useSubscriptionService();
  const { tenant, is_agency_managed } = useContext(TenantContext);

  const [has_subscription, setHasSubscription] = useState(false);
  const [show_action_menu, setShowActionMenu] = useState(false);
  const [show_create_audience_modal, setShowCreateAudienceModal] = useState(false);
  const [show_switch_account_modal, setShowSwitchAccountModal] = useState(false);
  const [show_topup_modal, setShowTopupModal] = useState(false);

  const menu_items = [
    [
      {
        icon_name: 'payments',
        label: 'Buy Credits',
        hide_if: (payload) => payload.is_agency_managed,
        onClick: () => {
          openTopupModal();
        }
      },
      {
        icon_name: 'payments',
        label: 'Upgrade Plan',
        hide_if: (payload) => payload.has_subscription || payload.is_agency_managed,
        onClick: () => {
          handleUpgrade();
        }
      }
    ],
    [
      {
        icon_name: 'add_business',
        label: 'Create Organisation',
        onClick: () => {
          history('/organizations');
        }
      },
      {
        icon_name: 'switch_account',
        label: 'Switch Organisation',
        onClick: () => {
          setShowSwitchAccountModal(true);
          closeMenuTray();
        }
      }
    ],
    [
      {
        icon_name: 'settings',
        label: 'Settings',
        onClick: () => {
          history('/settings');
          closeMenuTray();
        }
      }
    ],
    [
      {
        color: 'var(--red-primary)',
        icon_name: 'logout',
        label: <span style={{ fontWeight: 500 }}>Log out</span>,
        onClick: async () => {
          await logOut();
          dispatch(logout({}));
        }
      }
    ]
  ];

  useEffect(() => {
    if (open_tray) {
      openMenuTray();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open_tray]);

  useEffect(() => {
    fetchSubscription({
      query_string: 'plan_code=campaign&status=active'
    }).then(({ subscriptions: [subscription] }) => {
      setHasSubscription(subscription && subscription.id);
    });
  }, []);

  const closeMenuTray = () => {
    const tray = veil.current;
    let tray_left = 0;
    fadeOutTrayBackground();
    const slideOutAmination = setInterval(() => {
      if (tray_left > 100) {
        tray.style.left = '100%';
        clearInterval(slideOutAmination);
        onTrayClose();
        return;
      }

      tray_left += 10;
      tray.style.left = `-${tray_left}%`;
    }, 20);
  };

  const fadeInTrayBackground = () => {
    const tray = veil.current;
    let opacity = 0;
    const fadeInAnimation = setInterval(() => {
      if (opacity > 0.3) {
        tray.style.background = 'rgba(0, 0, 0, 0.35)';
        clearInterval(fadeInAnimation);
        return;
      }

      opacity += 0.1;
      tray.style.background = `rgba(0, 0, 0, ${opacity})`;
    }, 10);
  };

  const fadeOutTrayBackground = () => {
    const tray = veil.current;
    let opacity = 0.35;
    const fadeOutAnimation = setInterval(() => {
      if (opacity < 0) {
        tray.style.background = 'rgba(0, 0, 0, 0)';
        clearInterval(fadeOutAnimation);
        return;
      }

      opacity -= 0.1;
      tray.style.background = `rgba(0, 0, 0, ${opacity})`;
    }, 10);
  };

  const openMenuTray = () => {
    const tray = veil.current;
    let tray_left = 100;
    tray.style.background = 'rgba(0, 0, 0, 0)';
    const slideInAmination = setInterval(() => {
      if (tray_left < 0) {
        tray.style.left = 0;
        fadeInTrayBackground();
        clearInterval(slideInAmination);
        return;
      }

      tray_left -= 10;
      tray.style.left = `-${tray_left}%`;
    }, 20);
  };

  const openTopupModal = () => {
    closeMenuTray();
    setShowTopupModal(true);
  };

  /** */
  const handleUpgrade = () => {
    openModal();
    setModuleCode('campaign');
    closeMenuTray();
  };

  const renderItem = (item, id, metadata = {}) => {
    if (!item || (item.hide_if && item.hide_if(metadata))) return null;
    return (
      <div
        key={`action-menu-item-${id}`}
        className={styles.actionMenuItem}
        onClick={(e) => {
          item.onClick(e);
          setShowActionMenu(false);
        }}
      >
        <Icon
          width={16}
          height={16}
          className={styles.actionMenuItemIcon}
          name={item.icon_name}
          color={item.color}
        />
        <div className={styles.actionMenuItemLabel} style={{ color: item.color }}>
          {item.label}
        </div>
      </div>
    );
  };

  return (
    <nav className={styles.sideNavWrapper} ref={veil} onClick={() => closeMenuTray()}>
      <div className={styles.appSideNav} onClick={(e) => e.stopPropagation()}>
        <div>
          <header>
            <Link to="/dashboard" className={styles.link}>
              <span className={styles.siteIcon}>
                <SiteIcon width={24} height={24} />
              </span>
              {/* <div>
                <span className={styles.siteName}>Go-Mailer</span>
                <span className={styles.siteDot}>.</span>
              </div> */}
            </Link>
          </header>
          {/* <CreateButton actions={icon_new_actions} handleUserAction={handleUserAction} /> */}
          <section>
            {nav.map((nav_section) => {
              return nav_section.hide_if &&
                nav_section.hide_if({ permissions, isDesktop, isMiniDesktop }) ? null : (
                <div className={styles.navGroup} key={`nav-section-${nav_section.title}`}>
                  {nav_section.has_horizontal_rule && <hr />}
                  {/* <div className={styles.navGroupTitle}>{nav_section.title}</div> */}
                  <div>
                    {nav_section.items.map((item) =>
                      item.hide_if &&
                      item.hide_if({ permissions, isDesktop, isMiniDesktop }) ? null : (
                        <SideNavItem
                          key={`nav-item-${item.title}`}
                          {...item}
                          onItemClick={closeMenuTray}
                        />
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </section>
        </div>

        {show_action_menu && (
          <div className={styles.actionMenu}>
            {menu_items.map((group, id) => (
              <>
                {id > 0 && <hr />}
                <div className={styles.actionMenuGroup}>
                  {group.map((item, id) =>
                    renderItem(item, id, { has_subscription, is_agency_managed })
                  )}
                </div>
              </>
            ))}
          </div>
        )}
        <div className={styles.footer} onClick={() => setShowActionMenu((show) => !show)}>
          <div>
            <div className={styles.icon}>
              <img src={profile_image || profile_icon} alt="Profile Icon" />
            </div>
            <div className={styles.orgDetails}>
              <div className={styles.orgTitle}>{truncateText(tenant?.name, 15)}</div>
              <div className={styles.orgTenantId}>Tenant ID: {tenant?.id}</div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <GmModal
          title="Create Audience"
          show_title={true}
          show_modal={show_create_audience_modal}
          onClose={() => setShowCreateAudienceModal(false)}
        >
          <ListCreationForm />
        </GmModal>
        <GmModal
          bodyClassName={styles.refillModal}
          title="Refill Wallet"
          show_title
          show_modal={show_topup_modal}
          onClose={() => setShowTopupModal(false)}
        >
          <TopupForm />
        </GmModal>
        <GmModal
          title="Switch Organization"
          show_title={true}
          show_modal={show_switch_account_modal}
          onClose={() => setShowSwitchAccountModal(false)}
        >
          <SwitchOrganizationModal />
        </GmModal>
      </div>
    </nav>
  );
};

export default SideNav;
